import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { inject, observer } from 'mobx-react';
import { minutesAndSeconds } from "../Utils/timeFormat";
import PopupMenu from "./PopupMenu";
import { showToast } from '../Utils/toast';
import api from '../Utils/api';

import logo from "../Assets/logo.png";

const PlayIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const Playlist = forwardRef(({ playlist, onPlay, selectedTrack, preview, clearPreview, onClose, currentPlaylistId, PlaylistStore }, ref) => {
  const playlistEmpty = playlist.length === 0;
  const trackRefs = useRef(new Map());

  useImperativeHandle(ref, () => ({
    scrollToTrack: (mediaId) => {
      const element = trackRefs.current.get(mediaId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  }));

  return (
    <div className="flex flex-col h-full bg-primary">
      {/* Playlist Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden thin-scroll">
        {!playlistEmpty && (
          <ul className="list-none m-0 p-0">
            {playlist.map((track) => {
              const handleRowClick = () => {
                if (onPlay) onPlay(track.mediaId);
              };
              return (
              <li
                ref={(el) => {
                  if (el) {
                    trackRefs.current.set(track.mediaId, el);
                  } else {
                    trackRefs.current.delete(track.mediaId);
                  }
                }}
                className={`
                  flex border-b border-neutral-900 cursor-pointer transition-colors group
                  ${selectedTrack === track.mediaId ? 'bg-neutral-800' : 'hover:bg-neutral-900'}
                `}
                key={track.mediaId}
              >
                {/* Album Art */}
                <div
                  className="w-14 h-14 md:w-16 md:h-16 bg-cover bg-center flex-shrink-0"
                  style={{ backgroundImage: `url(${track.albumArtUrl})` }}
                  onClick={handleRowClick}
                />

                {/* Track Details */}
                <div
                  className="flex-1 flex flex-col justify-center min-w-0 px-2 md:px-3"
                  onClick={handleRowClick}
                >
                  <p className="m-0 text-text text-sm md:text-base truncate overflow-hidden">
                    {track.title}
                  </p>
                  <p className="m-0 text-text-muted text-xs md:text-sm truncate">
                    {track.artist}
                  </p>
                </div>

                {/* Track Metrics and Menu */}
                <div className="flex items-center gap-2 pr-2 md:pr-4 flex-shrink-0">
                  <div className="flex flex-col justify-center items-end">
                    <p className="m-0 text-text text-sm md:text-base font-mono tabular-nums">
                      {minutesAndSeconds(track.duration).join(":")}
                    </p>
                    <p className="m-0 text-text-muted text-xs md:text-sm flex items-center gap-1">
                      <span role="img" aria-label="headphones">🎧</span>
                      {track.playCount}
                    </p>
                  </div>

                  {/* 3-dot menu */}
                  <div
                    className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PopupMenu
                      trigger={
                        <button
                          className="
                            w-8 h-8 rounded-full flex items-center justify-center text-text-muted hover:text-text hover:bg-neutral-800 transition-all focus:outline-none focus:ring-1 focus:ring-accent
                          "
                          aria-label="Song options"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="5" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="12" cy="19" r="2" />
                          </svg>
                        </button>
                      }
                      items={[
                        {
                          label: 'Add to playlist',
                          icon: (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          ),
                          onClick: () => {
                            PlaylistStore.openAddToPlaylistModal(
                              {
                                mediaId: track.mediaId,
                                title: track.title,
                                artist: track.artist
                              },
                              currentPlaylistId
                            );
                          },
                          variant: 'default',
                        },
                        // Show "Remove from playlist" only if viewing a specific non-virtual playlist
                        currentPlaylistId && !['public', 'my-songs'].includes(currentPlaylistId) && {
                          label: `Remove from playlist`,
                          icon: (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          ),
                          onClick: async () => {
                            try {
                              await showToast.promise(
                                api.removeSongFromPlaylist(currentPlaylistId, track.mediaId),
                                {
                                  loading: 'Removing song...',
                                  success: 'Song removed',
                                  error: 'Failed to remove song',
                                }
                              );
                              // Refresh playlist
                              if (PlaylistStore.preview) {
                                await PlaylistStore.loadPlaylist(currentPlaylistId);
                              }
                            } catch (error) {
                              console.error('Failed to remove song:', error);
                            }
                          },
                          variant: 'danger',
                        },
                      ].filter(Boolean)}
                      position="bottom-right"
                    />
                  </div>
                </div>
              </li>
            );
            })}
          </ul>
        )}

        {/* Empty State */}
        {playlistEmpty && (
          <div className="flex flex-col items-center justify-center h-full pt-20 px-4">
            <p className="text-gray-400 text-3xl md:text-4xl mb-2 text-center">
              Bummer!
            </p>
            <p className="text-gray-400 text-lg md:text-xl text-center">
              Playlist empty
            </p>
          </div>
        )}
      </div>

      {/* Preview Mode Footer */}
      {preview && (
        <div className="bg-secondary p-3 md:p-4 flex items-center border-t border-neutral-800">
          <button
            onClick={() => {
              clearPreview();
            }}
            className="p-2 text-2xl md:text-3xl text-text-muted hover:text-text transition-colors"
            aria-label="Close preview"
          >
            ×
          </button>
          <div className="ml-2">
            <img
              src={logo}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full border border-neutral-700"
              alt="preview"
            />
          </div>
          <div className="flex-1 text-text ml-3 text-sm md:text-base">
            Preview
          </div>
          <div className="text-text">
            <PlayIcon className="w-12 h-12 md:w-14 md:h-14" />
          </div>
        </div>
      )}
    </div>
  );
});

export default inject('PlaylistStore')(observer(Playlist));
